import React from 'react';

interface MenuButtonProps {
  icon: React.ElementType;
  title: string;
  isSelected: boolean;
  isCollapsed: boolean;
  onClick: () => void;
  disabled?: boolean;
}

/**
 * MenuAccordionTrigger - A reusable button component for sidebar menu items
 * 
 * This component provides consistent styling and behavior for menu items
 * in the sidebar, handling both collapsed and expanded states as well as
 * selection states.
 */
const MenuAccordionTrigger: React.FC<MenuButtonProps> = ({
  icon: Icon,
  title,
  isSelected,
  isCollapsed,
  onClick,
  disabled
}) => {
  return (
    <button
      type="button"
      className={`w-full flex items-center py-1 px-2 rounded-md transition-colors duration-200 ${
        isSelected
          ? 'bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300 font-semibold'
          : 'hover:bg-gray-200 dark:hover:bg-gray-700'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      style={{ justifyContent: isCollapsed ? 'center' : 'space-between' }}
      onClick={onClick}
      disabled={disabled}
    >
      <div className="flex items-center space-x-3 min-w-0 flex-1">
        <Icon className="h-7 w-7 flex-shrink-0" />
        {!isCollapsed && <span className="font-medium text-base truncate min-w-0">{title}</span>}
      </div>
      
      {/* Only show chevron icon when not collapsed */}
      {!isCollapsed && (
        <div className="h-5 w-5 shrink-0 transition-transform">
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            className="h-5 w-5"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>
      )}
    </button>
  );
};

export default MenuAccordionTrigger; 